import { useState } from 'react';

import { api } from '../api/client';
import { Badge, Button, Card, EvidenceImage, Field, Loading, Notice, Table } from '../components';
import { useAction, useResource } from '../hooks';
import { useSession } from '../auth/session';
import { article } from '../vocabulary';

/**
 * "I am at this place and the app says I am not."
 *
 * A field user blocked by a geofence they are standing inside proposes a correction; the
 * person who runs the place decides. The screen exists because the alternative is a phone
 * call, and a phone call leaves nothing behind.
 *
 * The photographs are the substance. A single proposed coordinate is a number to be taken on
 * trust; several photographs, each carrying the position the phone reported when it was
 * taken, are something a manager can actually judge — and the spread between them says as
 * much as the distance from the stored pin does.
 */

interface LocationRequestRow {
  id: string;
  siteId: string;
  siteCode: string;
  siteName: string;
  storedLatitude: string | null;
  storedLongitude: string | null;
  proposedLatitude: string;
  proposedLongitude: string;
  distanceFromStoredM: string | null;
  accuracyM: string | null;
  note: string | null;
  status: string;
  raisedByName: string;
  createdAtServer: string;
  decisionNote: string | null;
  photographs: Array<{ evidenceId: string; latitude: string | null; longitude: string | null }>;
}

export function LocationRequests(): JSX.Element {
  const { config } = useSession();
  const [showAll, setShowAll] = useState(false);

  const requests = useResource(
    () => api.get<LocationRequestRow[]>(`/location-requests${showAll ? '?status=ALL' : ''}`),
    [showAll],
  );

  const [open, setOpen] = useState<LocationRequestRow | null>(null);

  return (
    <>
      <Card
        title="Location corrections"
        actions={
          <Button variant="secondary" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Show only pending' : 'Show all'}
          </Button>
        }
      >
        <p className="muted">
          Raised by {article(config.fieldUserLabel)} {config.fieldUserLabel.toLowerCase()} who
          was refused a check-in at a{' '}
          {config.siteLabel.toLowerCase()} they were standing at. Approving moves the{' '}
          {config.siteLabel.toLowerCase()}; rejecting leaves it where it is.
        </p>

        {requests.loading ? (
          <Loading />
        ) : requests.error !== null ? (
          <Notice kind="error">{requests.error}</Notice>
        ) : (
          <Table
            rows={requests.data ?? []}
            keyOf={(row) => row.id}
            empty="Nothing waiting."
            onRowClick={setOpen}
            columns={[
              { header: config.siteLabel, render: (row) => `${row.siteCode} — ${row.siteName}` },
              { header: 'Raised by', render: (row) => row.raisedByName },
              {
                header: 'Distance from stored',
                numeric: true,
                render: (row) =>
                  row.distanceFromStoredM === null
                    ? 'no position on file'
                    : `${Math.round(Number(row.distanceFromStoredM))} m`,
              },
              { header: 'Photographs', numeric: true, render: (row) => row.photographs.length },
              {
                header: 'Status',
                render: (row) => <Badge tone={row.status}>{row.status}</Badge>,
              },
            ]}
          />
        )}
      </Card>

      {open !== null && (
        <Decision
          request={open}
          onClose={() => setOpen(null)}
          onDecided={() => {
            setOpen(null);
            requests.reload();
          }}
        />
      )}
    </>
  );
}

function Decision({
  request,
  onClose,
  onDecided,
}: {
  request: LocationRequestRow;
  onClose: () => void;
  onDecided: () => void;
}): JSX.Element {
  const { run, error, busy } = useAction();
  const [note, setNote] = useState('');

  const decide = async (verdict: 'approve' | 'reject'): Promise<void> => {
    const ok = await run(async () => {
      await api.post(`/location-requests/${request.id}/${verdict}`, { note });
    });
    if (ok) onDecided();
  };

  const settled = request.status !== 'PENDING';

  return (
    <Card
      title={`${request.siteCode} — ${request.siteName}`}
      actions={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {error !== null && <Notice kind="error">{error}</Notice>}

      <div className="row">
        <Badge tone={request.status}>{request.status}</Badge>
        <span className="muted">raised by {request.raisedByName}</span>
      </div>

      <h3>Where it is recorded</h3>
      <p className="muted">
        {request.storedLatitude === null
          ? 'No position on file — this would be the first.'
          : `${request.storedLatitude}, ${request.storedLongitude}`}
      </p>

      <h3>Where they say it is</h3>
      <p className="muted">
        {request.proposedLatitude}, {request.proposedLongitude}
        {request.distanceFromStoredM !== null &&
          ` — ${Math.round(Number(request.distanceFromStoredM))} m away`}
        {request.accuracyM !== null &&
          ` · the phone reported ±${Math.round(Number(request.accuracyM))} m`}
      </p>

      {request.note !== null && <p>{request.note}</p>}

      <h3>Photographs</h3>
      {request.photographs.length === 0 ? (
        // Worth saying rather than showing an empty area: a request with no photographs is
        // one coordinate and a claim, and that should feel like thin evidence.
        <Notice kind="info">
          No photographs were attached. There is nothing here to check the position against.
        </Notice>
      ) : (
        <div className="stack">
          {request.photographs.map((photo) => (
            <div key={photo.evidenceId} className="row">
              <EvidenceImage evidenceId={photo.evidenceId} alt="Submitted from the place" height={160} />
              <span className="muted">
                {photo.latitude === null
                  ? 'no position recorded'
                  : `taken at ${photo.latitude}, ${photo.longitude}`}
              </span>
            </div>
          ))}
        </div>
      )}

      {settled ? (
        <Notice kind="info">
          Already {request.status.toLowerCase()}
          {request.decisionNote === null ? '' : `: ${request.decisionNote}`}
        </Notice>
      ) : (
        <>
          <Field
            label="Note"
            value={note}
            onChange={setNote}
            hint="Required to reject. Recorded on the audit trail either way."
          />

          <div className="form-actions">
            <Button onClick={() => void decide('approve')} disabled={busy}>
              Approve and move it
            </Button>
            <Button
              variant="danger"
              onClick={() => void decide('reject')}
              disabled={busy || note.trim() === ''}
            >
              Reject
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
